// src/components/shell/AppSidebar.js
// Sidebar "wahou" : pliable/dépliable, s'adapte jour/nuit, sections, carte utilisateur.
// Garde les mêmes routes/outils (voir navConfig.js). Design refait de zéro.
import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { PanelLeftClose, PanelLeftOpen, X, ChevronUp } from "lucide-react";
import { useTranslation } from "react-i18next";
import { MAIN_NAV, SYSTEM_NAV, SHELL_LABELS } from "./navConfig";
import useMe from "../../hooks/useMe";
import AccountSheet from "./AccountSheet";
import LogoFM from "../../assets/images/logofullmargin.png";

const COLLAPSE_KEY = "fm:sidebar-collapsed";
export const SIDEBAR_W_EXPANDED = 224;
export const SIDEBAR_W_COLLAPSED = 64;

// La nav doit être dépliée à CHAQUE chargement de page : on remet l'état à
// "déplié" au démarrage de l'app. La clé localStorage ne sert plus qu'à
// synchroniser la nav et les listes secondaires pendant la session en cours.
try {
  localStorage.setItem(COLLAPSE_KEY, "false");
} catch {}

function readCollapsed() {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === "true";
  } catch {
    return false;
  }
}

export default function AppSidebar({
  mobileOpen = false,
  onCloseMobile,
  dragOffset = null,
  drawerWidth = 288,
  isDesktop = true,
}) {
  const { i18n } = useTranslation();
  const lang = (i18n.language || "fr").startsWith("en") ? "en" : "fr";
  const t = SHELL_LABELS[lang];

  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [sheetOpen, setSheetOpen] = useState(false);
  const { user, initials } = useMe();

  // Sur mobile (drawer) : toujours déplié, quelle que soit la préférence desktop.
  const expanded = isDesktop ? !collapsed : true;

  // Drag en cours : le drawer suit le doigt, on coupe la transition CSS.
  const dragging = dragOffset !== null;
  const width = isDesktop ? (expanded ? SIDEBAR_W_EXPANDED : SIDEBAR_W_COLLAPSED) : drawerWidth;
  const scrimOpacity = dragging ? Math.max(0, (dragOffset + drawerWidth) / drawerWidth) : 1;

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, String(collapsed));
    } catch {}
    window.dispatchEvent(new Event("fm:sidebar-toggle"));
  }, [collapsed]);

  // Sync l'état plié depuis l'extérieur (ex: une autre sidebar qui se déplie).
  useEffect(() => {
    const sync = () => setCollapsed(readCollapsed());
    window.addEventListener("fm:sidebar-toggle", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("fm:sidebar-toggle", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  // Item de navigation (icône centrée quand plié, icône+label quand déplié).
  const NavItem = ({ item }) => {
    const Icon = item.icon;
    const label = t[item.labelKey] || item.labelKey;
    const base = `group/item relative flex items-center h-11 mx-2 rounded-xl transition-colors ${
      expanded ? "gap-3 px-3" : "justify-center px-0"
    }`;
    const inactive =
      "text-slate-500 hover:bg-slate-100 hover:text-violet-700 dark:text-indigo-200/70 dark:hover:bg-white/10 dark:hover:text-white";
    const active =
      "bg-violet-100 text-violet-700 dark:bg-white/15 dark:text-white";

    const inner = (isActive) => (
      <>
        {isActive && (
          <span className="absolute left-0 top-1/2 -translate-y-1/2 h-6 w-1 rounded-r-full bg-violet-500 dark:bg-violet-300" />
        )}
        {/* Le retour vers FullMargin porte le logo de la marque, pas une icône. */}
        {item.logo ? (
          <img src={LogoFM} alt="" className="h-5 w-5 shrink-0 object-contain" />
        ) : (
          <Icon size={20} className="shrink-0" />
        )}
        {expanded && <span className="truncate text-sm font-medium">{label}</span>}
      </>
    );

    // Outil annoncé mais pas encore livré : visible, non cliquable.
    if (item.soon) {
      return (
        <div
          title={`${label} — ${t.soon}`}
          aria-disabled="true"
          className={`${base} cursor-default text-slate-400 dark:text-indigo-200/40`}
        >
          <Icon size={20} className="shrink-0" />
          {expanded && (
            <span className="flex min-w-0 flex-1 items-center gap-2">
              <span className="truncate text-sm font-medium">{label}</span>
              <span className="shrink-0 rounded-full border border-slate-200 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-slate-400 dark:border-white/10 dark:text-indigo-200/50">
                {t.soon}
              </span>
            </span>
          )}
        </div>
      );
    }

    if (item.external) {
      return (
        <a href={item.href} title={label} className={`${base} ${inactive}`}>
          {inner(false)}
        </a>
      );
    }
    return (
      <NavLink
        to={item.to}
        end={item.end}
        onClick={onCloseMobile}
        title={label}
        className={({ isActive }) => `${base} ${isActive ? active : inactive}`}
      >
        {({ isActive }) => inner(isActive)}
      </NavLink>
    );
  };

  const SectionLabel = ({ children }) =>
    expanded ? (
      <div className="px-5 pt-4 pb-1 text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-indigo-300/50">
        {children}
      </div>
    ) : (
      <div className="mx-4 my-3 border-t border-slate-200 dark:border-white/5" />
    );

  return (
    <>
      {(mobileOpen || dragging) && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden"
          style={dragging ? { opacity: scrimOpacity, transition: "none" } : undefined}
          onClick={onCloseMobile}
        />
      )}

      <aside
        style={{
          width,
          ...(dragging ? { transform: `translateX(${dragOffset}px)`, transition: "none" } : null),
        }}
        className={[
          "fixed left-0 top-0 z-50 h-[100dvh] flex flex-col",
          // Jour : blanc. Nuit : violet foncé dégradé.
          "bg-white border-r border-slate-200 text-slate-700",
          "dark:bg-[#17143a] dark:bg-gradient-to-b dark:from-[#1c1746] dark:via-[#17143a] dark:to-[#120f2e] dark:border-white/5 dark:text-white",
          "transition-[width,transform] duration-300 ease-out",
          mobileOpen ? "translate-x-0" : "-translate-x-full",
          "lg:translate-x-0",
        ].join(" ")}
      >
        {/* Encoche / barre d'état (mobile plein écran). */}
        <div className="shrink-0 h-[env(safe-area-inset-top)] lg:hidden" />

        {/* Header : logo + bouton plier/déplier */}
        <div className={`flex items-center h-16 shrink-0 ${expanded ? "px-4" : "justify-center px-2"}`}>
          {expanded ? (
            <>
              <img
                src={LogoFM}
                alt="FullMargin"
                className="h-10 w-10 rounded-xl object-contain shrink-0"
              />
              <span className="ml-3 text-lg font-bold tracking-tight whitespace-nowrap text-slate-800 dark:text-white">
                Full<span className="text-violet-500 dark:text-violet-300">Metrix</span>
              </span>
              {isDesktop ? (
                <button
                  onClick={() => setCollapsed(true)}
                  className="ml-auto hidden lg:grid place-items-center h-9 w-9 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:text-indigo-200/60 dark:hover:bg-white/10 dark:hover:text-white"
                  title="Plier"
                  aria-label="Plier la barre"
                >
                  <PanelLeftClose size={18} />
                </button>
              ) : (
                <button
                  onClick={onCloseMobile}
                  className="fm-tap ml-auto grid place-items-center h-11 w-11 rounded-xl hover:bg-slate-100 dark:hover:bg-white/10 lg:hidden"
                  aria-label="Fermer"
                >
                  <X size={20} />
                </button>
              )}
            </>
          ) : (
            // Plié : le logo devient le bouton "déplier" au survol (swap d'icône).
            <button
              onClick={() => setCollapsed(false)}
              className="group relative grid place-items-center h-10 w-10 rounded-xl overflow-hidden"
              title={t.expandSidebar}
              aria-label={t.expandSidebar}
            >
              <img
                src={LogoFM}
                alt="FullMargin"
                className="h-10 w-10 object-contain transition-opacity duration-150 group-hover:opacity-0"
              />
              <span className="absolute inset-0 grid place-items-center rounded-xl bg-violet-500 opacity-0 transition-opacity duration-150 group-hover:opacity-100">
                <PanelLeftOpen size={20} className="text-white" />
              </span>
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto overflow-x-hidden py-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <SectionLabel>{t.sectionMain}</SectionLabel>
          {MAIN_NAV.map((item) => (
            // Les entrées « bientôt » n'ont pas de route : la clé prend le libellé.
            <NavItem key={item.to || item.labelKey} item={item} />
          ))}
          <SectionLabel>{t.sectionSystem}</SectionLabel>
          {SYSTEM_NAV.map((item) => (
            <NavItem key={item.to || item.href} item={item} />
          ))}
        </nav>

      </aside>

      <AccountSheet open={sheetOpen} onClose={() => setSheetOpen(false)} />
    </>
  );
}

// Largeur réservée côté contenu (desktop) selon l'état plié/déplié.
export function useSidebarWidth() {
  const [collapsed, setCollapsed] = useState(readCollapsed);
  useEffect(() => {
    const onChange = () => setCollapsed(readCollapsed());
    window.addEventListener("storage", onChange);
    window.addEventListener("fm:sidebar-toggle", onChange);
    return () => {
      window.removeEventListener("storage", onChange);
      window.removeEventListener("fm:sidebar-toggle", onChange);
    };
  }, []);
  return collapsed ? SIDEBAR_W_COLLAPSED : SIDEBAR_W_EXPANDED;
}
